﻿import $ from 'jquery';
import 'jquery-highlight';
import Element from '../utilities/element';
import KeyCode from '../utilities/keycode';
import { SearchField } from "./searchfield"
import { KeyboardScrollable, IKeyboardScrollableOptions } from "../utilities/keyboardscrollable";
import Ajax from "../modules/remote/ajax";
import "../utilities/extensions";
import "./popover";

declare global {
  interface JQuery {
    highlight(pattern: string);
    unhighlight()
    suggestsearch: (option) => any;
  }
}

export class SuggestSearch {

  static get wireup(): string { return '[data-suggestsearch]'; }

  private $element: JQuery;
  private groupId: string;
  private popover: SelectableListPopover;
  private isSelect: boolean;
  private $selectValueInput: JQuery;
  private selectedHasBeenSet: boolean;

  public constructor($element: JQuery, options: any) {
    this.setElement($element);
    this.setGroupId();
    this.setUpDisabledAndReadonlyFunctionality();
    this.buildPopover();
    this.wireupSearchField();
    this.wireupKeyboardListeners();

    if (this.$element.data('is-select'))
      this.configureToBehaveLikeSelect();
    else
      this.popover.disable();
  }

  private setElement($element: JQuery) {
    if (!$element || $element.length != 1)
      throw new Error("You must provide a single element to Rustig.SuggestSearch");
    this.$element = $element;
  }

  private setGroupId() {
    this.groupId = this.$element.data('ss-group');
    if (!this.groupId)
      throw new Error('Provided element must have attribute [data-ss-group]');
  }

  private setUpDisabledAndReadonlyFunctionality(): void {
    this.$element.on('click.rustig.suggestsearch', (e: Event) => {
      if (this.$element.isDisabled() || this.$element.isReadonly())
        e.stopImmediatePropagation();
    });
  }

  private buildPopover(): void {
    this.popover = new SelectableListPopover(this.$element, 'ss-suggestions');
    this.popover.setOnHidden(() => this.selectedHasBeenSet = false);
  }

  private wireupSearchField(): void {
    this.$element.searchField({
      action: this.fetchSuggestions.bind(this)
    });
  }

  private fetchSuggestions(): void {
    let searchText = this.$element.val();

    if (!searchText && !this.isSelect) {
      this.popover.clear().disable();
      return;
    }

    this.popover.enable();

    Ajax.execute({
      url: this.$element.data('url'),
      success: this.onSuccess.bind(this),
      data: this.getData()
    });
  }

  private onSuccess(data: string[], textStatus: string, jqXHR: JQueryXHR): void {
    this.popover.clear();
    let searchText = this.$element.val() as string;

    if (data && data.length)
      this.buildSuggestions(data, searchText);
    else if (this.isSelect) {
      this.$selectValueInput.val(null);
      this.addSuggestion(this.buildSelectListItem());
    }
    else
      this.addSuggestion(this.suggestionWithText(searchText));

    if (this.$element.is(':focus'))
      this.popover.open();
  }

  private buildSuggestions(data: string[], searchText: string): void {
    for (let i = 0; i < data.length; i++) {
      let $suggestion = $(data[i]);
      this.addSuggestion($suggestion);
    }

    if (searchText)
      this.popover.highlight(searchText);
  }

  private addSuggestion($element: JQuery, isSelected?: boolean): void {
    $element.addClass('ss-suggestion');

    this.wireupMouseClickForSuggestion($element);

    if (isSelected) {
      $element.attr('data-selected', 'true');
    }

    this.popover.addItem($element);
  }

  private wireupMouseClickForSuggestion($element: JQuery) {
    $element.on('click.rustig.suggestsearch', this.onSelectByMouseClick.bind(this));
  }

  private onSelectByMouseClick(event: JQueryEventObject): void {
    let $selected = <JQuery> $(event.target).closest('.ss-suggestion');
    this.setSelected($selected);
  }

  private setSelected($selected: JQuery) {
    if (this.selectedHasBeenSet)
      return;

    this.selectedHasBeenSet = true;

    if (this.$element.val() === '') {
      this.clearValueForInputsInGroup();
      this.popover.clear();
      this.clearSuggestionsAndSelectionCache();

      if ($selected === undefined || $selected.length === 0) {
        this.selectedHasBeenSet = false;
      }

      return;
    }

    let selectedIndex = $selected.index();
    this.setValueForInputsInGroup($selected);
    this.popover.setSelected(selectedIndex).close();

    let searchText = this.$element.val() as string;
    if (searchText)
      this.popover.highlight(searchText);

    this.cacheSuggestionsAndSelection(selectedIndex);
  }

  private setValueForInputsInGroup($selected: JQuery): void {
    $('ss-group'.asDataSelector(this.groupId)).each(function () {
      let $element = $(this);
      let key = $element.data('ss-selected-key');

      let $hasKey = $selected.find('ss-key'.asDataSelector(key));
      if ($hasKey.length > 0)
        $element.val($hasKey.attr('value')).trigger('change');
    });
  }

  private clearValueForInputsInGroup(): void {
    $('ss-group'.asDataSelector(this.groupId)).each(function () {
      $(this).val(null).trigger('change');
    });
  }

  private buildSelectListItem(text?: string, value?: any): JQuery {
    let textValue = text ? 'value="' + text + '"' : null;
    let valueValue = text ? 'value="' + value + '"' : null;
    return $('<div class="grid"><input type="hidden" data-ss-key="Text" ' + textValue + '/><input type="hidden" data-ss-key="Value"' + valueValue + '/><div class="gu-1 ss-select-item">' + (text || '&nbsp;') + '</div></div>');
  }

  private suggestionWithText(text: string): JQuery {
    return $('<div class="grid"><div class="gu-1 ss-select-item">' + text + '</div></div>');
  }

  private getData(): any {
    let data = {};
    let name = this.$element.data('ss-name') || 'searchText';
    data[name] = this.$element.val();

    let $containsSelectors = this.isSelect ? this.$selectValueInput : this.$element;
    let fieldsIncludedInSearch = $containsSelectors.data('remote-data-additions');
    if (fieldsIncludedInSearch) {
      $(fieldsIncludedInSearch).each(function () {
        let element = new Element($(this));
        let fieldName = element.$element.data('ss-name') || element.name();
        data[fieldName] = element.value();
      });
    }

    return data;
  }

  private wireupKeyboardListeners(): void {
    this.$element.on('selected.rustig.keyboardscrollable', this.onSelectByKeyboard.bind(this))
                 .on('keydown.rustig.suggestsearch', this.onKeyDown.bind(this));
  }

  private onSelectByKeyboard(event: Event, selected: HTMLElement): void {
    let $selected = $(selected);
    this.setSelected($selected);
  }

  private onKeyDown(event: any): void {
    if (event.keyCode === KeyCode.Escape) {
      this.onEscapePressed();
    }
    else if (event.keyCode === KeyCode.Up || event.keyCode === KeyCode.Down)
      this.popover.open();
    else if (event.keyCode === KeyCode.Tab)
      this.popover.close();
  }

  private configureToBehaveLikeSelect(): void {
    this.isSelect = true;

    this.$selectValueInput = $('ss-group'.asDataSelector(this.groupId) + '[data-ss-selected-key="Value"]');
    if (this.$selectValueInput.length <= 0)
      throw new Error('An input with the same group and key "value" must exist for if the data-is-select attribute is present');

    this.$selectValueInput.copyFieldBooleanAttributes(this.$element);
    let self = this;

    this.$element.on('change.rustig', function () {
      if ($(this).val() === '') {
        self.setSelected($());
      }

      return false;
    });
    
    this.popover.setOnHide(function (popoverIsClearing: boolean) {
      if (!popoverIsClearing)
        self.setSelected(self.popover.getSelected());
    });
    this.createDropdownButton()

    this.addSuggestion(this.buildSelectListItem(this.$element.val() as string, this.$selectValueInput.val()), true);

    let fieldsIncludedInSearch = this.$selectValueInput.data('remote-data-additions');
    if (fieldsIncludedInSearch) {
      $(document).on('change.rustig.suggestsearch', fieldsIncludedInSearch, () => {
        this.$selectValueInput.val(null);
        this.$element.val(null);
        this.$element.searchField('setValue', null);
        this.popover.clear();
        this.addSuggestion(this.buildSelectListItem("", ""));
      });
    }
  }

  private createDropdownButton(): void {
    let $button = $(null);
    let hideButton = false;
    if (!hideButton) {
      $button = $('<button type="button"></button>')
        .attr('tabindex', '-1')
        .addClass('button button-icon dropdown-button')
        .click(this.onButtonClick.bind(this))
        .insertAfter(this.$element);
    }
  }

  private onButtonClick(): boolean {
    if (this.$element.isDisabled() || this.$element.isReadonly())
      return true;

    if (this.popover.isOpen())
      return true;
    else {
      this.$element.focus();
      this.popover.open();
      return false;
    }
  }

  private onEscapePressed() {
    this.popover.clear();
    this.restoreSuggestionsAndSelection();
  }

  private clearSuggestionsAndSelectionCache() {
    this.$element.data('selected-index', -1);
    this.$element.data('selected-suggestion-items', $());
  }

  private cacheSuggestionsAndSelection(selectedIndex: number) {
    let $suggestionItems = this.$element.closest('.suggest-search').find('.ss-suggestion');

    this.$element.data('selected-index', selectedIndex);
    this.$element.data('selected-suggestion-items', $suggestionItems);
  }

  private restoreSuggestionsAndSelection() {
    let selectedIndex = this.$element.data('selected-index') as number;
    if (selectedIndex < 0)
      return;

    let $suggestionItems = this.$element.data('selected-suggestion-items') as JQuery;
    let $suggestSearchPopoverContent = this.$element.closest('.suggest-search').find('[data-suggest-search-popover-content]');

    $suggestSearchPopoverContent.html('')
                                .append($suggestionItems);
    let $selected = $suggestionItems.eq(selectedIndex);
    this.setValueForInputsInGroup($selected);

    let self = this;
    $suggestSearchPopoverContent.find('.ss-suggestion')
      .each(function () {
        self.wireupMouseClickForSuggestion($(this));
      });
  }
}

class SelectableListPopover {

  private $element: JQuery;
  private $popover: JQuery;
  private keyboardScrollable: KeyboardScrollable;
  private rootClass: string;
  private channel: string = 'selectablelist';
  private _isOpen: boolean;
  private isClearing: boolean;

  public constructor($element: JQuery, rootClass: string) {
    this.setElement($element);
    this.rootClass = rootClass;
    this.createPopover();
  }

  private setElement($element: JQuery) {
    if (!$element || $element.length != 1)
      throw new Error("You must provide a single element to Rustig.SelectableListPopover");
    this.$element = $element;
  }

  private createPopover(): void {
    let id = Math.random().toString();
    let $temp = $('<div data-popover-content="' + id + '"></div>').insertAfter(this.$element);

    this.$popover = $('<div class="popover ' + this.rootClass + '" data-suggest-search-popover-content="on"></div>');
    this.$popover.appendTo($temp);

    this.$element
      .popover({
        popover: id,
        trigger: 'click',
        channel: this.channel,
        placement: 'bottom',
        containerClass: null,
        alignment: 'left',
        sizing: 'element'
      })
      .on('shown.rustig.popover.suggestsearch', this.onShown.bind(this))
      .on('hidden.rustig.popover.suggestsearch', this.onHidden.bind(this));
  }

  private onShown(event: Event, args: any) {
    if (!args || args.channel !== this.channel)
      return;

    this._isOpen = true;

    let options = this.buildKeyboardScrollableOptions();
    if (!this.keyboardScrollable) {
      this.keyboardScrollable = new KeyboardScrollable(this.$element, options);
    }
    else {
      this.keyboardScrollable.updateScrollableContainer(options);
    }
  }

  private buildKeyboardScrollableOptions(): IKeyboardScrollableOptions {
    let $globalPopover = $('#popoverGlobalMenu' + 'channel'.asDataSelector(this.channel));
    let options: IKeyboardScrollableOptions = { scrollableContainer: $globalPopover.find('[data-suggest-search-popover-content]') };

    let $selected = this.$element.closest('.suggest-search')
                                 .find('.ss-suggestion')
                                 .filter('[data-selected]');
    if ($selected.length) {
      options.scrollIndex = $selected.index();
      this.scrollTo($selected);
    }
    else
      options.scrollIndex = 0;

    return options;
  }

  private scrollTo($element: JQuery) {
    let $parent = $element.parent();
    $parent.scrollTop(
      $element.offset().top - $parent.offset().top + $parent.scrollTop()
    );
  }

  private onHidden(event: Event, args: any) {
    if (!args || args.channel !== this.channel)
      return;

    this._isOpen = false;
  }

  public setOnHide(callback: (popoverIsClearing: boolean) => void): void {
    let self = this;
    this.$element.on('hide.rustig.popover.suggestsearch', function (event: any, args: any) {
      if (!args || args.channel !== self.channel)
        return;

      callback(self.isClearing);
    });
  }

  public setOnHidden(callback: () => void): void {
    let self = this;
    this.$element.on('hidden.rustig.popover.suggestsearch', function (event: any, args: any) {
      if (!args || args.channel !== self.channel)
        return;

      callback();
    });
  }

  public open(): SelectableListPopover {
    if (this._isOpen)
      return this;

    this.$element.trigger('click.rustig.popover');
    return this;
  }

  public close(): SelectableListPopover {
    if (!this._isOpen)
      return this;

    this.$element.popover('close', this.channel);
    return this;
  }

  public enable(): SelectableListPopover {
    this.$element.popover('enable', this.channel);
    return this;
  }

  public disable(): SelectableListPopover {
    this.$element.popover('disable', this.channel);
    return this;
  }

  public clear(): SelectableListPopover {
    this.isClearing = true;
    this.$popover.html('');
    this.close();
    this.isClearing = false;
    return this;
  }

  public addItem($item: JQuery): SelectableListPopover {
    this.$popover.append($item);
    return this;
  }

  public isOpen(): boolean {
    return this._isOpen;
  }

  public highlight(searchText: string): SelectableListPopover {
    this.$popover.unhighlight();
    this.$popover.highlight(searchText);

    return this;
  }

  public getSelected(): JQuery {
    return this.keyboardScrollable.getSelected();
  }

  public setSelected(index: number): SelectableListPopover {
    this.$popover.find('[data-selected]').removeAttr('data-selected');
    this.$popover.children().eq(index).attr('data-selected', 'true');
    return this;
  }
}

function Plugin(option) {
  return this.each(function () {
    var $this = $(this);
    var data = $this.data('rustig.suggestsearch');
    var options = typeof option == 'object' && option;

    if (!data) {
      data = new SuggestSearch($this, options);
      $this.data('rustig.suggestsearch', data);
    }
    if (typeof option == 'string')
      data[option]();
  });
}
$.fn.suggestsearch = Plugin;
